import { Button } from "react-bootstrap";
import { ClientSearchResult } from "../../types";
import { AppButton } from "../../component/AppButton";
import * as yup from "yup";

export interface NavItem {
  id: string;      // This must exactly match the HTML div id
  label: string;   // The text displayed on the sidebar button
}

export const DASHBOARD_SECTIONS: NavItem[] = [
  { id: 'PatientInfo', label: 'PatientInfo' },
  { id: 'PatientContactInfo', label: 'PatientContactInfo' },
  { id: 'MedicalInfo', label: 'MedicalInfo' },
  { id: 'TestInfo', label: 'TestInfo' }
];

interface DataTableColumn {
  name: React.ReactNode;
  selector?: (row: any) => any;
  cell?: (row: any) => React.ReactNode;
  button?: boolean;
}

export interface RoleData {
  id: number;
  value: string;
}

export type SourceSelectOption = {
  label: string;
  value: string;
};

export const clientSearchCols = (
  clickHandler: React.Dispatch<React.SetStateAction<ClientSearchResult>>,
  showActionColumn: boolean,
  targetRef: React.RefObject<HTMLDivElement | null>,
  showPatientInfo: boolean
): DataTableColumn[] => {
  const columns: DataTableColumn[] = [
    {
      name: <b>First Name</b>,
      selector: (row: any) => row.firstName,
    },
    {
      name: <b>Middle Name</b>,
      selector: (row: any) => row.middleName,
    },
    {
      name: <b>Last Name</b>,
      selector: (row: any) => row.lastName,
    },
    {
      name: <b>Date of Birth</b>,
      selector: (row: any) => row.dob,
    },
    {
      name: <b>Birth Sex</b>,
      selector: (row: any) => row.birthGender,
    },
  ];

  if (showActionColumn) {
    columns.push({
      name: <b style={{ display: "block", textAlign: "center", width: "100%" }}>Action</b>,
      button: true,
      cell: (row: ClientSearchResult) => (
        <div style={{padding:'5px'}}>
          <AppButton 
            variant={"primary"} 
            onClick={() => {
              clickHandler(row);
              
              let user=showPatientInfo;
              setTimeout(() => {
                const element = targetRef?.current;
                if (element) {
                  // 1. Move programmatic focus to the div
                  element.focus({ preventScroll: true }); 

                  // 2. Calculate coordinates relative to the viewport + current scroll
                  const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                  //const elementPosition = element.getBoundingClientRect().top + window.scrollY;
                  
                  // 3. Subtract 50px for padding/header clearance
                  const offsetPosition = elementPosition -240;

                  // 4. Smoothly scroll to the calculated position
                  window.scrollTo({
                    top: 315.6500244140625,
                    behavior: "smooth"
                  });
                }
              }, 500);
            }}
          >
            Details
          </AppButton>
          &nbsp;
        </div>
      ),
    });
  }

  return columns;
};
// const SpecimenIDValidation = (specimenDate: string) => yup.string()
// .when(specimenDate,{
//   is: (val:any) => !!val,
//   then: (schema) =>
//      schema
//       .required('Specimen ID is required when date is entered')
//       .matches(/^[a-zA-Z0-9]*$/,'Only alphanumeric characters are allowed')
//       .max(80,'Maximum 80 characters allowed'),
//   otherwise: (schema) => schema.notRequired(),
// })
const SpecimenIDValidation = yup.string()
  .matches(/^[a-zA-Z0-9]*$/, 'Only alphanumeric characters are allowed')
  .max(80, 'Maximum 80 characters allowed')

const POSITIVE_NAAT_VALUE = "Positive";
const DISCLOSED_VALUE = "Yes";
const POSITIVE_HIVAgAB_VALUE = "Positive";
const InCare_Yes_VALUE = "Yes";
const POSITIVE_HIVIa_VALUE = "Positive";
export type caseReportSchema = yup.InferType<typeof NewCaseReportValidationSchema>;

export const NewCaseReportValidationSchema = yup.object().shape({
  // Schema should be in sequence for proper validation error display.
  isNew: yup.boolean(),
  Source: yup.string(),
  routinizedHospitalId: yup
    .string()
    .required("Routinized Testing Focus Project - Hospital is Required")
    .notOneOf(["0"], "Routinized Testing Focus Project - Hospital is Required"),

  routinizedFacilityId: yup
    .string()
    .required("Routinized Testing Focus Project - Facility is Required")
    .notOneOf(["0"], "Routinized Testing Focus Project - Facility is Required"),

  dateReceived: yup.string()
    .required("Date Received is required")
    .test(
      "dob-not-in-future",
      "Date Received is not valid",
      (value) => {
        if (!value) return false;
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  medicalRecordNumber: yup
    .string()
    .required("Medical Record Number is required")
    .max(15, "MRN must be 15 characters or less"),


  firstName: yup.string()
    .test(
      "required-if-isNew",
      "First Name is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ),
  lastName: yup.string()
    .test(
      "required-if-isNew",
      "Last Name is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ),

  dateOfBirth: yup
    .string()
    .test(
      "required-if-isNew",
      "Date of Birth is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    )
    .test(
      "dob-not-in-future",
      "Date of birth is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  clientInfoSourceClientId: yup.string(),

  // The fields you're making conditional
  lastViralLoad: yup.string().when('clientInfoSourceClientId', {
    is: (val: string | any[]) => val && val.length > 0, // Checks if clientInfoSourceClientId has a value
    then: (schema) => schema.required("Date of last viral load is required"), // Required if it has a value
    otherwise: (schema) => schema.notRequired(), // Not required otherwise
  }),

  updatedlastCd4: yup.string().when('clientInfoSourceClientId', {
    is: (val: string | any[]) => val && val.length > 0, // Checks if clientInfoSourceClientId has a value
    then: (schema) => schema.required("Date of Last CD4 Count is required"), // Required if it has a value
    otherwise: (schema) => schema.notRequired(), // Not required otherwise
  })
    .test(
      "dob-not-in-future",
      "Date of last CD4 is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),


  updatedLastViralLoad: yup.string().when('clientInfoSourceClientId', {
    is: (val: string | any[]) => val && val.length > 0, // Checks if clientInfoSourceClientId has a value
    then: (schema) => schema.required("Date of last viral load is required"), // Required if it has a value
    otherwise: (schema) => schema.notRequired(), // Not required otherwise
  })
    .test(
      "dob-not-in-future",
      "Date of last viral load is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  newlastViralLoad: yup.string()
    .test(
      "required-if-isNew",
      "Date of last viral load is required",
      function (value) {
        const { isNew, clientInfoSourceClientId } = this.parent;
        if (isNew && clientInfoSourceClientId && clientInfoSourceClientId.length > 0 && !value) {
          return false;
        }
        return true;
      }
    ).test(
      "dob-not-in-future",
      "Date of last viral load is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  newlastCd4: yup.string()
    .test(
      "required-if-isNew",
      "Date of Last CD4 Count is required",
      function (value) {
        const { isNew, clientInfoSourceClientId } = this.parent;
        if (isNew && clientInfoSourceClientId && clientInfoSourceClientId.length > 0 && !value) {
          return false;
        }
        return true;
      }
    )
    .test(
      "dob-not-in-future",
      "Date of Last CD4 Count is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),



  NewCaseLast4SSN: yup
    .string()
    .matches(/^(|.{5,})$/, "Atleast 4 digit SSN required")
    .optional(),

  //addressTypeDetail: yup.string().required("Address Type is required client info"),
  addressTypeDetailNewCase: yup.string().required("Address Type is required"),

  streetAddress: yup
    .string()
    .nullable()
    .test(
      "required-if-new-and-addressTypeDetailNewCase",
      "Street Address is required",
      function (value) {
        const { isNew, addressTypeDetailNewCase } = this.parent;
        const shouldValidate = isNew === true && addressTypeDetailNewCase === "Yes";
        if (shouldValidate && (!value || value.trim() === "")) {
          return false;
        }
        return true;
      }
    ),

  city: yup
    .string()
    .nullable()
    .test(
      "required-if-new-and-addressTypeDetailNewCase",
      "City is required",
      function (value) {
        const { isNew, addressTypeDetailNewCase } = this.parent;
        const shouldValidate = isNew === true && addressTypeDetailNewCase === "Yes";

        if (shouldValidate && (!value || value.trim() === "")) {
          return false;
        }

        return true;
      }
    ),


  stateId: yup
    .string()
    .nullable()
    //.min(1, "State is required")
    .required("State is required")
    .optional(),
  zipcode: yup
    .string()
    .nullable()
    .test(
      "required-if-new-and-addressTypeDetailNewCase",
      "At least 5 digit Zip Code is required",
      function (value) {
        const { isNew, addressTypeDetailNewCase } = this.parent;
        const shouldValidate = isNew === true && addressTypeDetailNewCase === "Yes";

        if (shouldValidate) {
          if (!value || value.trim().length < 5) {
            return false;
          }
        }

        return true;
      }
    ),

  countyId: yup
    .string()
    .nullable()
    // .min(1, "County is required")
    .required("County is required")
    .optional(),

  datePresented: yup.string()
    .test(
      "required-if-isNew",
      "Date of Presented To is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ).test(
      "dob-not-in-future",
      "Date of Presented is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  chiefComplaint: yup.string()
    .test(
      "required-if-isNew",
      "Chief Complaint is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ),

  listOfSymptoms: yup.string()
    .test(
      "required-if-isNew",
      "List of Symptoms are required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ),

  admittedToHospital: yup
    .string()
    .test(
      "required-if-isNew",
      "Admitted to Hospital is Required",
      function (value) {
        const { isNew } = this.parent;
        if (isNew && !value) {
          return false;
        }
        return true;
      }
    ),
  admittedToHospitalWhereClient: yup.string(),


  admittedToHospitalWhere: yup.string(),

  isChecked: yup.boolean(),

  dateOfTest: yup.string()
    .nullable()
    .when("isChecked", {
      is: false,
      then: (schema) =>
        schema.required("Date of Test is required").typeError("Invalid date"),
      otherwise: (schema) => schema.nullable(),
    })
    .test(
      "dob-not-in-future",
      "Date of Test is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  rprSpecimenID: SpecimenIDValidation,
  vdrlSpecimenID: SpecimenIDValidation,
  tppaSpecimenID: SpecimenIDValidation,
  ftaabsSpecimenID: SpecimenIDValidation,
  chlamydiaSpecimenID: SpecimenIDValidation,
  gonorrheaSpecimenID: SpecimenIDValidation,

  testRPR: yup.string(),

  tppa: yup.string(),

  ftaAbs: yup.string(),

  Equine_infectious_anemia: yup.string(),

  Chlamydia: yup.string(),
  Gonorrhea: yup.string(),
  EditCaseBirthGender: yup.string(),

  selfReported: yup.boolean(),
  caseReportCheckBox: yup.boolean(),
  section1: yup.boolean(),
  section2: yup.boolean(),
  section3: yup.boolean(),

  NAAT: yup.string(),

  hivAgAb: yup
    .string(),

  hivIASupplemental: yup
    .string(),

  Disclosed: yup
    .string(),

  NewCaseInCare: yup
    .string(),

  NewCaseBirthGender: yup.string().optional(),

  NewCaseIsPregnant: yup
    .string()
    .nullable()
    .when(["NewCaseBirthGender"], {
      is: (val: any) => val == "Female",
      then: (schema) =>
        schema
          .required("Select an option")
          .typeError("Select an option"),
    }),


  EditCaseIsPregnant: yup.string().when('EditCaseBirthGender', {
    is: (val: any, ) => val?.toLowerCase() == "female",
    then: (schema) => schema.required("Select an option"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  EditCaseLMPDate: yup
    .string()
    .nullable()
    .when(['EditCaseBirthGender', 'EditCaseIsPregnant'], {
      is: (birthGender: any, pregnantId: any) => birthGender?.toLowerCase() == "female" && pregnantId == "1",
      then: (schema) =>
        schema
          .required("LMP Date is Required")
          .typeError("LMP Date is Required"),
    })
    .test(
      "dob-not-in-future",
      "LMP Date is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  NewCaseLMPDate: yup
    .string()
    .nullable()
    .when(["NewCaseBirthGender", "NewCaseIsPregnant"], {
      is: (birthGender: any, pregnantId: any) => birthGender == "Female" && pregnantId == "1",
      then: (schema) =>
        schema
          .required("LMP Date is Required")
          .typeError("LMP Date is Required"),
    })
    .test(
      "dob-not-in-future",
      "LMP Date is not valid",
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  dateOfDisCharge: yup
    .string()
    .test(
      'date-validation',
      'Date of Discharge is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  rprCollectionDate: yup.string().when('rPR_Type', {
    is: (type: string) => type && type !== '0' && type !== '',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test('date-validation', 'Specimen collection date is required', value =>
          !!value && new Date(value) <= today && new Date(value) >= minDate
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test('date-validation', 'RPR Date is not valid', value =>
          !value || (new Date(value) <= today && new Date(value) >= minDate)
        ),
  }),


  vdrlCollectionDate: yup.string().when('vDRL_Type', {
    is: (type: string) => !!type && type !== '0',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test(
          'date-validation',
          'VDRL Date is not valid',
          value => {
            if (!value) return false; // Missing date when required: show "required" error.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test(
          'date-validation',
          'VDRL Date is not valid',
          value => {
            if (!value) return true; // Optional field.
            const inputDate = new Date(value);
            return inputDate <= today && inputDate >= minDate; // Valid date in range.
          }
        )
  }),




  tppaCollectionDate: yup.string().when('tPPA_Type', {
    is: (type: string) => type && type !== '0' && type !== '',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test('date-validation', 'Specimen collection date is required', value =>
          !!value && new Date(value) <= today && new Date(value) >= minDate
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test('date-validation', 'TP-PA Date is not valid', value =>
          !value || (new Date(value) <= today && new Date(value) >= minDate)
        ),
  }),

  ftaabsCollectionDate: yup.string().when('fTAABS_Type', {
    is: (type: string) => type && type !== '0' && type !== '',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test('date-validation', 'Specimen collection date is required', value =>
          !!value && new Date(value) <= today && new Date(value) >= minDate
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test('date-validation', 'FTA-ABS Date is not valid', value =>
          !value || (new Date(value) <= today && new Date(value) >= minDate)
        ),
  }),

  eIAIgGSpecimenCollectionDate: yup.string().when('eIAIgG_Type', {
  is: (type: string) => type && type !== '0' && type !== '',
  then: (schema) =>
    schema
      .required('Specimen collection date is required')
      .test(
        'date-validation',
        'Specimen collection date is required',
        (value) =>
          !!value &&
          new Date(value) <= today &&
          new Date(value) >= minDate
      ),
  otherwise: (schema) =>
    schema
      .notRequired()
      .nullable()
      .default(undefined)
      .test(
        'date-validation',
        'EIA IgG Date is not valid',
        (value) =>
          !value ||
          (new Date(value) <= today &&
            new Date(value) >= minDate)
      ),
}),

  chlamydiaCollectionDate: yup.string().when('chlamydia_Type', {
    is: (type: string) => type && type !== '0' && type !== '',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test('date-validation', 'Specimen collection date is required', value =>
          !!value && new Date(value) <= today && new Date(value) >= minDate
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test('date-validation', 'Chlamydia Date is not valid', value =>
          !value || (new Date(value) <= today && new Date(value) >= minDate)
        ),
  }),

  gonorrheaCollectionDate: yup.string().when('gonorrhea_Type', {
    is: (type: string) => type && type !== '0' && type !== '',
    then: (schema) =>
      schema.required('Specimen collection date is required')
        .test('date-validation', 'Specimen collection date is required', value =>
          !!value && new Date(value) <= today && new Date(value) >= minDate
        ),
    otherwise: (schema) =>
      schema.notRequired()
        .nullable()
        .default(undefined)
        .test('date-validation', 'Gonorrhea Date is not valid', value =>
          !value || (new Date(value) <= today && new Date(value) >= minDate)
        ),
  }),

 naat: yup
  .string()
  .nullable()
  .when(["PositiveTestResult", "CaseReportCheckBox"], {
    is: (
      positiveTestResult: boolean,
      checkBox: boolean
    ) =>
      positiveTestResult === true &&
      checkBox === false,

    then: (schema) =>
      schema
        .required("NAAT is required")
        .test(
          "naat-validation",
          "NAAT is required",
          (value) => value !== "0"
        ),

    otherwise: (schema) => schema,
  }),
      naatDate: yup
        .string() // 1. Use .date() for proper date validation
        .nullable() // 2. Allow it to be null/empty unless required
        .when('NAAT', {
          // Condition: If naat equals POSITIVE_NAAT_VALUE
          is: (naatValue: string) => naatValue === POSITIVE_NAAT_VALUE,
    
          // THEN: Apply required and date range constraints
          then: (schema) => schema
            .required("NAAT Date is required") // 3. Make it required
            .test(
              'date-validation',
              'Naat Date is not valid',
              (value) => {
                if (!value) return true; // This field is not a required one
                const inputDate = new Date(value);
                return inputDate <= today && inputDate >= minDate;
              }
            ),
    
          // OTHERWISE: Do not apply any extra validation (it remains nullable)
          otherwise: (schema) => schema
        }),
  disclosedDate: yup
     .string() // 1. Use .date() for proper date validation
     .nullable() // 2. Allow it to be null/empty unless required
     .when('Disclosed', {
       // Condition: If naat equals POSITIVE_NAAT_VALUE
       is: (disclosureValue: string) => disclosureValue === DISCLOSED_VALUE,
 
       // THEN: Apply required and date range constraints
       then: (schema) => schema
         .required("Disclosed Date is required") // 3. Make it required
         .test(
           'date-validation',
           'Disclosure Date is not valid',
           (value) => {
             if (!value) return true; // This field is not a required one
             const inputDate = new Date(value);
             return inputDate <= today && inputDate >= minDate;
           }
         ),
 
       // OTHERWISE: Do not apply any extra validation (it remains nullable)
       otherwise: (schema) => schema
     }),
  hivAgAbDate: yup
     .string() // 1. Use .date() for proper date validation
     .nullable() // 2. Allow it to be null/empty unless required
     .when('hivAgAb', {
       // Condition: If naat equals POSITIVE_NAAT_VALUE
       is: (hivagabValue: string) => hivagabValue === POSITIVE_HIVAgAB_VALUE,
 
       // THEN: Apply required and date range constraints
       then: (schema) => schema
         .required("HIV AG AB Date is required") // 3. Make it required
         .test(
           'date-validation',
           'HIV AG AB Date is not valid',
           (value) => {
             if (!value) return true; // This field is not a required one
             const inputDate = new Date(value);
             return inputDate <= today && inputDate >= minDate;
           }
         ),
 
       // OTHERWISE: Do not apply any extra validation (it remains nullable)
       otherwise: (schema) => schema
     }),
   inCareDate: yup
      .string() // 1. Use .date() for proper date validation
      .nullable() // 2. Allow it to be null/empty unless required
      .when('NewCaseInCare', {
        // Condition: If naat equals POSITIVE_NAAT_VALUE
        is: (incareValue: string) => incareValue === InCare_Yes_VALUE,
  
        // THEN: Apply required and date range constraints
        then: (schema) => schema
          .required("In Care Date is required") // 3. Make it required
          .test(
            'date-validation',
            'In Care Date is not valid',
            (value) => {
              if (!value) return true; // This field is not a required one
              const inputDate = new Date(value);
              return inputDate <= today && inputDate >= minDate;
            }
          ),
  
        // OTHERWISE: Do not apply any extra validation (it remains nullable)
        otherwise: (schema) => schema
      }),
 hivIaSupplementalDate: yup
     .string() // 1. Use .date() for proper date validation
     .nullable() // 2. Allow it to be null/empty unless required
     .when('hivIASupplemental', {
       // Condition: If naat equals POSITIVE_NAAT_VALUE
       is: (hivIaValue: string) => hivIaValue === POSITIVE_HIVIa_VALUE,
 
       // THEN: Apply required and date range constraints
       then: (schema) => schema
         .required("HIV IA Date is required") // 3. Make it required
         .test(
           'date-validation',
           'HIV - 1/2 IA Date is not valid',
           (value) => {
             if (!value) return true; // This field is not a required one
             const inputDate = new Date(value);
             return inputDate <= today && inputDate >= minDate;
           }
         ),
       // OTHERWISE: Do not apply any extra validation (it remains nullable)
       otherwise: (schema) => schema
     }),
  hivDiagnosisDate: yup
    .string()
    .test(
      'date-validation',
      'HIV Diagnosis Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedHivDiagnosisDate: yup
    .string()
    .test(
      'date-validation',
      'HIV Diagnosis Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  aidsDate: yup
    .string()
    .test(
      'date-validation',
      'Aids Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedAidsDate: yup
    .string()
    .test(
      'date-validation',
      'Aids Date is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  dateReportedHivPositive: yup
    .string()
    .test(
      'date-validation',
      'Date Reported HIV Positive is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
  updatedDateReportedHivPositive: yup
    .string()
    .test(
      'date-validation',
      'Date Reported HIV Positive is not valid',
      (value) => {
        if (!value) return true; // This field is not a required one
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),

  //STD Info Updated
  // UpdatedRprType: yup.string().when('Source', {
  //   is: (val: string) =>{
  //     console.log('RPR Type Source Value:', val);
  //     return val && val !== "HIV";
  //   }, 
  //   then: (schema) => schema.required("RPR Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedRprType: yup.string(),

  UpdatedRprType1: yup.string().when('UpdatedRprType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedRprSpecimenCollectionDate: yup.string().when(['UpdatedRprType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // UpdatedRprSpecimenId: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Medical Record Number is required").max(25, "MRN must be 15 characters or less"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedRprDetails: yup.string().when(['UpdatedRprType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),


  // VDRL Test Fields 
  UpdatedVdrlType: yup.string(),

  // UpdatedVdrlType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("VDRL Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedVdrlType1: yup.string().when('UpdatedVdrlType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedVdrlSpecimenCollectionDate: yup.string().when(['UpdatedVdrlType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedVdrlDetails: yup.string().when(['UpdatedVdrlType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // TP-PA Test Fields 
  UpdatedTpPaType: yup.string(),

  // UpdatedTpPaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("TP-PA Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedTpPaType1: yup.string().when('UpdatedTpPaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedTpPaSpecimenCollectionDate: yup.string().when(['UpdatedTpPaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedTpPaDetails: yup.string().when(['UpdatedTpPaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),


  // FTA-ABS Test Fields 
  UpdatedFtaAbsType: yup.string(),
  // UpdatedFtaAbsType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("FTA-ABS Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedFtaAbsType1: yup.string().when('UpdatedFtaAbsType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedFtaAbsSpecimenCollectionDate: yup.string().when(['UpdatedFtaAbsType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedFtaAbsDetails: yup.string().when(['UpdatedFtaAbsType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // EIA Test Fields
UpdatedEiaType: yup.string(),

UpdatedEiaType1: yup.string().when('UpdatedEiaType', {
  is: 'Other',
  then: (schema) => schema.required("Specify Other Test"),
  otherwise: (schema) => schema.notRequired().nullable().default(undefined),
}),

UpdatedEiaSpecimenCollectionDate: yup.string().when(['UpdatedEiaType'], {
  is: (type: any) => type && type !== '0' && type !== '',
  then: (schema) => schema.required("Specimen Collection date is required"),
  otherwise: (schema) => schema.notRequired().nullable().default(undefined),
}),

UpdatedEiaDetails: yup.string().when(['UpdatedEiaType'], {
  is: (type: any) => type && type !== '0' && type !== '',
  then: (schema) =>
    schema
      .required("Detail is required")
      .notOneOf(["0"], "Detail is required"),
  otherwise: (schema) => schema.notRequired().nullable().default(undefined),
}),

  // Chlamydia Test Fields
  UpdatedChlamydiaType: yup.string(),

  // UpdatedChlamydiaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Chlamydia Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedChlamydiaType1: yup.string().when('UpdatedChlamydiaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedChlamydiaDate: yup.string().when(['UpdatedChlamydiaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedChlamydiaDetails: yup.string().when(['UpdatedChlamydiaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // Gonorrhea Test Fields
  UpdatedGonorrheaType: yup.string(),

  // UpdatedGonorrheaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Gonorrhea Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedGonorrheaType1: yup.string().when('UpdatedGonorrheaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedGonorrheaSpecimenCollectionDate: yup.string().when(['UpdatedGonorrheaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedGonorrheaDetails: yup.string().when(['UpdatedGonorrheaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  //STD Section (New Case Report)
  rPR_Type: yup.string(),

  rPR_Result: yup.string().when(['rPR_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  vDRL_Type: yup.string(),

  vDRL_Result: yup.string().when(['vDRL_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  tPPA_Type: yup.string(),

  tPPA_Result: yup.string().when(['tPPA_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  fTAABS_Type: yup.string(),

  fTAABS_Result: yup.string().when(['fTAABS_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  eIAIgG_Type: yup.string(),

  eIAIgG_Result: yup.string().when(['eIAIgG_Type'], {
  is: (type: any) => type && type !== '0' && type !== '',
  then: (schema) => schema.required("Detail is required"),
  otherwise: (schema) => schema.notRequired().nullable().default(undefined),
}),

  chlamydia_Type: yup.string(),

  chlamydia_Result: yup.string().when(['chlamydia_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  gonorrhea_Type: yup.string(),

  gonorrhea_Result: yup.string().when(['gonorrhea_Type'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

});

export const ClientInfoValidationSchema = (isEditingContactInfo: boolean) => yup.object().shape({
  // clientInfoFirstName: yup.string().required("First Name is required"),
  // clientInfoLastName: yup.string().required("Last Name is required"),
  // clientInfoDateOfBirth: yup.string().required("Date of Birth is required"),
  routinizedHospitalId: yup
    .string()
    .required("Routinized Testing Focus Project - Hospital is Required"),

  routinizedFacilityId: yup
    .string()
    .required("Routinized Testing Focus Project - Facility is Required")
    .notOneOf(["0"], "Routinized Testing Focus Project - Facility is Required"),

  dateReceived: yup.string().required("Date Received is required"),
  medicalRecordNumber: yup
    .string()
    .required("Medical Record Number is required")
    .max(15, "MRN must be 15 characters or less"),

  addressTypeDetail: isEditingContactInfo
    ? yup.string().required("Address Type is required")
    : yup.string(),
  //.oneOf(["Yes", "No"], "Address Type details is Required"),

  clientInfoStreetAddress:
    yup.string()
      .nullable()
      .when(["addressTypeDetail"], {
        is: (val: any, isEditing: boolean) => isEditing && val == "Yes",
        then: (schema) =>
          schema
            .required("Street Address is required")
            .typeError("Street Address is required"),
      }),

  clientInfoCity: yup
    .string()
    .nullable()
    .when(["addressTypeDetail"], {
      is: (val: any, isEditing: boolean) => isEditing && val == "Yes",
      then: (schema) =>
        schema.required("City is required").typeError("City is required"),
    }),

  clientInfoZipcode: yup
    .string()
    .nullable()
    .when(["addressTypeDetail"], {
      is: (val: any, isEditing: boolean) => isEditing && val == "Yes",
      then: (schema) =>
        schema
          .required("Zip Code is required")
          // .min(5, "Atleast 5 digit Zip Code is required")
          .typeError("Zip Code is required"),
    }),

  state: yup
    .number()
    .nullable()
    .min(1, "State is required")
    .required("State is required")
    .optional(),

  county: yup
    .number()
    .nullable()
    .min(1, "County is required")
    .required("County is required")
    .optional(),

  clientInfoSourceClientId: yup.string(),

  admittedToHospitalWhereClient: yup
    .string()
    .nullable()
    .when(["admittedToHospital"], {
      is: (val: any) => val == "Yes",
      then: (schema) =>
        schema
          .required("Hospital Name is required")
          .typeError("Hospital Name is required"),
    }),

  updatedlastCd4: yup.string().when('clientInfoSourceClientId', {
    is: (val: string | any[]) => val && val.length > 0, // Checks if clientInfoSourceClientId has a value
    then: (schema) => schema.required("Date of Last CD4 Count is required"), // Required if it has a value
    otherwise: (schema) => schema.notRequired(), // Not required otherwise
  }),


  updatedLastViralLoad: yup.string().when('clientInfoSourceClientId', {
    is: (val: string | any[]) => val && val.length > 0, // Checks if clientInfoSourceClientId has a value
    then: (schema) => schema.required("Date of last viral load is required"), // Required if it has a value
    otherwise: (schema) => schema.notRequired(), // Not required otherwise
  }),

  //STD Info Updated
  // UpdatedRprType: yup.string().when('Source', {
  //   is: (val: string) =>{
  //     console.log('RPR Type Source Value:', val);
  //     return val && val !== "HIV";
  //   }, 
  //   then: (schema) => schema.required("RPR Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedRprType: yup.string(),

  UpdatedRprType1: yup.string().when('UpdatedRprType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedRprSpecimenCollectionDate: yup.string().when(['UpdatedRprType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // UpdatedRprSpecimenId: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Medical Record Number is required").max(25, "MRN must be 15 characters or less"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedRprDetails: yup.string().when(['UpdatedRprType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),


  // VDRL Test Fields 
  UpdatedVdrlType: yup.string(),

  // UpdatedVdrlType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("VDRL Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedVdrlType1: yup.string().when('UpdatedVdrlType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedVdrlSpecimenCollectionDate: yup.string().when(['UpdatedVdrlType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedVdrlDetails: yup.string().when(['UpdatedVdrlType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // TP-PA Test Fields 
  UpdatedTpPaType: yup.string(),

  // UpdatedTpPaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("TP-PA Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedTpPaType1: yup.string().when('UpdatedTpPaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedTpPaSpecimenCollectionDate: yup.string().when(['UpdatedTpPaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedTpPaDetails: yup.string().when(['UpdatedTpPaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),


  // FTA-ABS Test Fields 
  UpdatedFtaAbsType: yup.string(),
  // UpdatedFtaAbsType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("FTA-ABS Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedFtaAbsType1: yup.string().when('UpdatedFtaAbsType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedFtaAbsSpecimenCollectionDate: yup.string().when(['UpdatedFtaAbsType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedFtaAbsDetails: yup.string().when(['UpdatedFtaAbsType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),


  // Chlamydia Test Fields
  UpdatedChlamydiaType: yup.string(),

  // UpdatedChlamydiaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Chlamydia Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),
  UpdatedChlamydiaType1: yup.string().when('UpdatedChlamydiaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedChlamydiaDate: yup.string().when(['UpdatedChlamydiaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedChlamydiaDetails: yup.string().when(['UpdatedChlamydiaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

  // Gonorrhea Test Fields
  UpdatedGonorrheaType: yup.string(),

  // UpdatedGonorrheaType: yup.string().when('Source', {
  //   is: (val: string) => val && val !== "HIV",
  //   then: (schema) => schema.required("Gonorrhea Test Type is required"),
  //   otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  // }),

  UpdatedGonorrheaType1: yup.string().when('UpdatedGonorrheaType', {
    is: 'Other',
    then: (schema) => schema.required("Specify Other Test"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedGonorrheaSpecimenCollectionDate: yup.string().when(['UpdatedGonorrheaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Specimen Collection date is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),
  UpdatedGonorrheaDetails: yup.string().when(['UpdatedGonorrheaType'], {
    is: (type: any) => type && type !== '0' && type !== '',
    then: (schema) => schema.required("Detail is required").notOneOf(["0"], "Detail is required"),
    otherwise: (schema) => schema.notRequired().nullable().default(undefined),
  }),

});

const today = new Date();
const minDate: Date = new Date('1850-01-01');
const maxDate: Date = new Date('2150-01-01');

export const ClientSearchValidationSchema = yup.object().shape({
  clientSearchFirstName: yup
    .string()
    .test(
      'clientSearchFirstName required check',
      'First Name is required',
      function (value) {
        const { clientSearchLastName } = this.parent;
        return value?.trim() || clientSearchLastName?.trim();
      }
    )
    .test(
      'clientSearchFirstName minimum check',
      'Must be at least 2 characters',
      function (value) {
        if (!value?.trim()) return true;
        return value?.trim().length >= 2;
      }
    ),
  clientSearchLastName: yup
    .string()
    .test(
      'clientSearchLastName required check',
      'Last Name is required',
      function (value) {
        const { clientSearchFirstName } = this.parent;
        return value?.trim() || clientSearchFirstName?.trim();
      }
    )
    .test(
      'clientSearchLastName minimum check',
      'Must be at least 2 characters',
      function (value) {
        if (!value?.trim()) return true;
        return value?.trim().length >= 2;
      }
    ),
  clientSearchDateOfBirth: yup
    .string()
    .required("Date of Birth is required")
    .test(
      "dob-not-in-future",
      "Date of Birth is not valid",
      (value) => {
        if (!value) return false;
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    ),
});

export const wildCardSearchValidationSchema = yup.object().shape({
  clientSearchFirstName: yup
    .string()
    .required('First Name is required')
    .min(2, 'Must be at least 2 characters')
    .test(
      'contains-star-if-three',
      'Please enter valid firstname or must include (*)',
      (value) => {
        if (!value) return false;
        if (value.length === 2) {
          return value.includes('*');
        }
        return true; // Valid if more than 2 characters
      }
    ),
  clientSearchDateOfBirth: yup.string()
    .required("Date of Birth is required")
    .test(
      "dob-not-in-future",
      "Date of Birth is not valid",
      (value) => {
        if (!value) return false;
        const inputDate = new Date(value);
        return inputDate <= today && inputDate >= minDate;
      }
    )
})

export interface UserDetails {
  roleName: string;
  routinizedHospitalName: string;
  routinizedHospitalId: number;
}
